export type ShareResult = 'native' | 'clipboard' | 'cancelled'

/**
 * Open the OS share sheet on mobile (Web Share API) or fall back to
 * clipboard copy on desktop / unsupported browsers.
 *
 * Capability check: both navigator.share and navigator.canShare must exist
 * and canShare must accept the payload before we attempt the native path.
 */
export async function shareLink({
  title,
  url,
}: {
  title: string
  url: string
}): Promise<ShareResult> {
  const payload = { title, url }

  if (
    typeof navigator.share === 'function' &&
    typeof navigator.canShare === 'function' &&
    navigator.canShare(payload)
  ) {
    try {
      await navigator.share(payload)
      return 'native'
    } catch (err) {
      if ((err as Error).name === 'AbortError') return 'cancelled'
      // Non-abort error (e.g. NotAllowedError) — fall through to clipboard
    }
  }

  await navigator.clipboard.writeText(url)
  return 'clipboard'
}

export type ShareFileResult = 'native' | 'downloaded' | 'cancelled'

/**
 * Share an image file via the OS share sheet (Web Share API level 2, files support)
 * or fall back to triggering a download + copying the caption text to the clipboard.
 *
 * Capability check: navigator.canShare must be called with the actual files payload —
 * browsers that support navigator.share but not file sharing return false here, so we
 * never assume file-share support from navigator.share existing alone.
 */
export async function shareImageFile({
  blob,
  filename,
  title,
  text,
}: {
  blob: Blob
  filename: string
  title: string
  text: string
}): Promise<ShareFileResult> {
  const file = new File([blob], filename, { type: blob.type })

  if (
    typeof navigator.share === 'function' &&
    typeof navigator.canShare === 'function' &&
    navigator.canShare({ files: [file] })
  ) {
    try {
      await navigator.share({ files: [file], title, text })
      return 'native'
    } catch (err) {
      if ((err as Error).name === 'AbortError') return 'cancelled'
      // Non-abort error — fall through to the download fallback below.
    }
  }

  downloadBlob(blob, filename)
  try {
    await navigator.clipboard.writeText(text)
  } catch {
    // Clipboard may be unavailable/blocked (permissions, insecure context) — the
    // download still succeeded, so this is not treated as a failure.
  }
  return 'downloaded'
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
